/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import QSModal from '../modal';
import { QSButton, QSIconButton, QSTypography } from '../../atoms';
import { Close, Search } from '@mui/icons-material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  WabaChannelRating,
  WabaChannelStatus,
  WabaIntegration,
  WabaIntegrationPaginationParams,
} from '../../../typings/waba';
import { BORDER_COLOR } from '../../../typings';
import QSTextInput from '../text-input';
import debounce from '../../../utils/debounce';
import { Box, ButtonBase, Icon } from '@mui/material';
import { Virtuoso } from 'react-virtuoso';
import CheckIcon from '@mui/icons-material/Check';
import Loader from '../loader';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import WabaQualityRatingHigh from '../../../icons/WabaQualityRatingHigh';
import WabaQualityRatingLow from '../../../icons/WabaQualityRatingLow';
import WabaQualityRatingMedium from '../../../icons/WabaQualityRatingMedium';
import { getWabaIntegrationQualityRatingStyles } from '../../../utils/wabaQualityRatingStyles';
import { DEFAULT_MEMBER, UNLIMITED, WABA_SELECTOR_MODAL } from '../../../constants';
import WabaIntegrationIcon from '../waba-integration-logo';
import usePaginatedQuery from '../../../hooks/usePaginatedQuery';
import { getWabaIntegrations } from '../../../requests/waba';
import { wabaIntegrationsAtomFamily } from '../../../states/waba';

export interface WabaSelectorModalExtraProps {
  headerTitle?: string;
  showCheckForAlreadySelected?: boolean;
  selectedWaba?: WabaIntegration;
}

interface WabaSelectorModalProps {
  open: boolean;
  onClose: () => any;
  setSelectedWaba: (waba: WabaIntegration) => any;
  allowMultiple?: boolean;
  customOnConfirm?: (waba: WabaIntegration) => any;
  wabaSelectorModalExtraProps?: WabaSelectorModalExtraProps;
  hideInactiveWabas?: boolean;
  onlyAssignedIntegrations?: boolean;
}

const WabaSelectorModal = ({
  open,
  onClose,
  setSelectedWaba,
  allowMultiple = false,
  hideInactiveWabas,
  onlyAssignedIntegrations,
  customOnConfirm,
  wabaSelectorModalExtraProps,
}: WabaSelectorModalProps) => {
  const [selectedWabas, setSelectedWabas] = useState<WabaIntegration | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string | undefined>('');

  const wabaIntegrationsGetter = useCallback(
    async (paginationParams?: WabaIntegrationPaginationParams) => {
      const normalizedSearchTerm =
        typeof searchTerm === 'string' && searchTerm.trim().length > 0 ? searchTerm.trim() : '';
      if (!paginationParams) {
        paginationParams = {
          sortAfter: undefined,
          searchTerm: normalizedSearchTerm,
          status: hideInactiveWabas ? WabaChannelStatus.ACTIVE : undefined,
          disconnectedFromMeta: hideInactiveWabas ? false : null,
          sendOnlyAssignedIntegrations: onlyAssignedIntegrations,
          hasMore: false,
        };
      }
      const data = await getWabaIntegrations(paginationParams);
      const { integrations, nextSortAfter, hasMore } = data || {};

      return {
        data: integrations,
        paginationData: {
          ...paginationParams,
          sortAfter: nextSortAfter ?? paginationParams?.nextSortAfter,
          searchTerm: normalizedSearchTerm,
          status: hideInactiveWabas ? WabaChannelStatus.ACTIVE : undefined,
          disconnectedFromMeta: hideInactiveWabas ? false : null,
          sendOnlyAssignedIntegrations: onlyAssignedIntegrations,
          hasMore: hasMore ?? paginationParams.hasMore,
        } as WabaIntegrationPaginationParams,
      };
    },
    [searchTerm, hideInactiveWabas, onlyAssignedIntegrations]
  );

  const wabaIntegrationsAtom = useMemo(() => {
    return wabaIntegrationsAtomFamily({
      id: WABA_SELECTOR_MODAL,
    });
  }, []);

  const {
    fetchMore: wabaIntegrationsFetchMore,
    requestData: {
      loading: wabaIntegrationsLoading,
      isFetchingMore: fetchingMoreWabaIntegrations,
      data: wabaIntegrationsData,
    },
  } = usePaginatedQuery<WabaIntegration, WabaIntegrationPaginationParams>({
    requestAtom: wabaIntegrationsAtom,
    getter: wabaIntegrationsGetter,
  });

  const selectWaba = useCallback((wabaIntegration: WabaIntegration) => {
    setSelectedWabas(wabaIntegration);
  }, []);

  const onConfirm = useCallback(() => {
    if (!selectedWabas) {
      return;
    }
    if (customOnConfirm) {
      customOnConfirm(selectedWabas);
    }
    setSelectedWaba(selectedWabas);
    onClose();
  }, [customOnConfirm, onClose, selectedWabas, setSelectedWaba]);

  const checkIfWabaIsSelected = useCallback(
    (wabaIntegration: WabaIntegration) => {
      if (Array.isArray(selectedWabas)) {
        return selectedWabas.findIndex((waba) => waba.id === wabaIntegration.id) !== -1;
      }

      if (!allowMultiple) {
        return selectedWabas?.id === wabaIntegration.id;
      }

      return false;
    },
    [allowMultiple, selectedWabas]
  );

  const debouncedSetSearch = debounce(setSearchTerm, 300);

  useEffect(() => {
    if (wabaSelectorModalExtraProps?.showCheckForAlreadySelected && wabaSelectorModalExtraProps?.selectedWaba) {
      setSelectedWabas(wabaSelectorModalExtraProps.selectedWaba);
    }
  }, [wabaSelectorModalExtraProps?.selectedWaba, wabaSelectorModalExtraProps?.showCheckForAlreadySelected]);

  return (
    <QSModal open={open} onClose={onClose}>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          position: absolute;
          top: 50%;
          border-radius: 4px;
          overflow: hidden;
          left: 50%;
          transform: translate(-50%, -50%);
          background: white;
          outline: none;
          &:focus-within {
            outline: none;
          }
          width: 500px;
          height: 500px;
          max-height: calc(100vh - 45px);
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 8px 16px;
            border-bottom: 1px solid ${BORDER_COLOR};
          `}
        >
          <QSTypography
            css={css`
              font-weight: bold;
            `}
          >
            {wabaSelectorModalExtraProps?.headerTitle ?? 'Send message via'}
          </QSTypography>
          <QSIconButton css={css``} sx={{}} onClick={onClose}>
            <Close />
          </QSIconButton>
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            align-self: stretch;
            flex: 1;
          `}
        >
          <div
            css={css`
              display: flex;
              padding: 12px;
              flex-direction: column;
              align-items: flex-start;
              gap: 12px;
              align-self: stretch;
              border-bottom: 1px solid ${BORDER_COLOR};
            `}
          >
            <div
              css={css`
                width: 100%;
              `}
            >
              <QSTextInput
                sx={{ width: '100%' }}
                onChange={(e) => debouncedSetSearch(e.target.value)}
                defaultValue={searchTerm}
                placeholder="Search"
                textInputProps={{
                  sx: {
                    background: 'white',
                    border: `1px solid ${BORDER_COLOR}`,
                    borderRadius: '6px',
                    minHeight: '35px',
                    justifyContent: 'center',
                    padding: '0 8px',
                    '.MuiInputBase-root::before': {
                      borderBottom: 'none !important',
                    },
                    '.MuiInputBase-root,.MuiInput-root': {
                      '&::before': {
                        border: 'none',
                      },
                      '&::after': {
                        border: 'none',
                      },
                      '&:hover:not(.Mui-disabled)::before': {
                        border: 'none',
                      },
                      'input::placeholder, textArea::placeholder': {
                        color: '#7B7B7B',
                      },
                    },
                  },
                  InputProps: {
                    startAdornment: (
                      <Icon
                        css={css`
                          display: flex;
                          align-items: center;
                        `}
                      >
                        <Search
                          css={css`
                            width: 20px;
                            height: 20px;
                          `}
                        />
                      </Icon>
                    ),
                    // endAdornment: searchTerm ? (
                    //   <IconButton
                    //     onClick={() => {
                    //       setSearchTerm('');
                    //     }}
                    //   >
                    //     <Close height={16} width={16} />
                    //   </IconButton>
                    // ) : null,
                  },
                  className: 'nofocus',
                  autoFocus: true,
                  autoComplete: 'off',
                  onKeyDown: (e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  },
                }}
              />
            </div>
          </div>
          <div
            css={css`
              flex: 1;
              width: 100%;
            `}
          >
            <Virtuoso<WabaIntegration>
              endReached={wabaIntegrationsFetchMore}
              totalCount={wabaIntegrationsData?.length}
              data={wabaIntegrationsData}
              itemContent={(index, wabaIntegration) => {
                if (!wabaIntegration || !wabaIntegration.id) {
                  return null;
                }
                return (
                  <ButtonBase<'div'>
                    css={css`
                      display: flex;
                      padding: 12px 20px;
                      justify-content: space-between;
                      align-items: center;
                      align-self: stretch;
                      border-bottom: 1px solid ${BORDER_COLOR};
                      width: 100%;
                      ${checkIfWabaIsSelected(wabaIntegration)
                        ? css`
                            background: rgba(93, 183, 121, 0.1);
                          `
                        : ''}
                    `}
                    onClick={() => selectWaba(wabaIntegration)}
                    component="div"
                  >
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: 16px;
                        flex: 1 0 0;
                      `}
                    >
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          gap: 16px;
                          flex: 1;
                        `}
                      >
                        <WabaIntegrationIcon
                          wabaIntegrationLogo={wabaIntegration.logo ?? ''}
                          wabaIntegrationName={
                            wabaIntegration.displayName ?? wabaIntegration.name ?? wabaIntegration.phoneNumber ?? ''
                          }
                          channelType={wabaIntegration.type}
                        />
                        <div
                          css={css`
                            display: flex;
                            flex-direction: column;
                            align-items: flex-start;
                            gap: 8px;
                          `}
                        >
                          <div
                            css={css`
                              display: flex;
                              align-items: center;
                              gap: 8px;
                            `}
                          >
                            <QSTypography
                              css={css`
                                font-size: 14px;
                                font-style: normal;
                                font-weight: 500;
                                line-height: normal;
                              `}
                            >
                              {wabaIntegration.displayName ?? wabaIntegration.name ?? wabaIntegration.phoneNumber ?? ''}
                            </QSTypography>
                            {wabaIntegration.qualityRating && (
                              <Box
                                css={css(
                                  getWabaIntegrationQualityRatingStyles(wabaIntegration.qualityRating),
                                  css`
                                    display: flex;
                                    flex-wrap: wrap;
                                    justify-content: flex-start;
                                    align-items: center;
                                    column-gap: 4px;
                                    padding: 3px 9px;
                                    border-radius: 12px;
                                  `
                                )}
                              >
                                {wabaIntegration.qualityRating === WabaChannelRating.HIGH ? (
                                  <WabaQualityRatingHigh
                                    css={css`
                                      margin-top: 4px;
                                    `}
                                  />
                                ) : wabaIntegration.qualityRating === WabaChannelRating.LOW ? (
                                  <WabaQualityRatingLow />
                                ) : wabaIntegration.qualityRating === WabaChannelRating.MEDIUM ? (
                                  <WabaQualityRatingMedium />
                                ) : null}
                                <QSTypography
                                  css={css`
                                    font-size: 12px;
                                    font-weight: 500;
                                    word-wrap: break-word;
                                    word-break: break-all;
                                  `}
                                >
                                  {wabaIntegration.qualityRating}
                                </QSTypography>
                              </Box>
                            )}
                          </div>
                          <QSTypography
                            css={css`
                              font-size: 14px;
                              font-style: normal;
                              font-weight: 500;
                              line-height: normal;
                            `}
                          >
                            {wabaIntegration.phoneNumber}
                          </QSTypography>
                          {(typeof wabaIntegration.messagingLimit === 'number' ||
                            wabaIntegration.messagingLimit === UNLIMITED) && (
                            <Box
                              css={css`
                                display: flex;
                                align-items: center;
                                gap: 8px;
                              `}
                            >
                              <MessageOutlinedIcon
                                css={css`
                                  height: 16px;
                                  min-height: 16px;
                                  width: 16px;
                                  min-width: 16px;
                                  align-self: flex-start;
                                  color: #7a7a7a;
                                `}
                              />
                              <QSTypography
                                css={css`
                                  color: #7a7a7a;
                                  font-size: 14px;
                                  font-style: normal;
                                  font-weight: 500;
                                  line-height: normal;
                                `}
                              >
                                Message limit:{' '}
                                <span
                                  css={css`
                                    color: #2d2d2d;
                                  `}
                                >
                                  {wabaIntegration.messagingLimit.toString()} / 24hrs
                                </span>
                              </QSTypography>
                            </Box>
                          )}
                        </div>
                      </div>
                      {checkIfWabaIsSelected(wabaIntegration) ? (
                        <Icon>
                          <CheckIcon
                            css={css`
                              color: #538d6f;
                            `}
                          />
                        </Icon>
                      ) : null}
                    </div>
                  </ButtonBase>
                );
              }}
              overscan={20}
              increaseViewportBy={20}
              components={{
                EmptyPlaceholder: () => {
                  return (
                    <div
                      css={css`
                        height: 100%;
                        width: 100%;
                        display: flex;
                        flex-direction: column;
                        column-gap: 8px;
                        justify-content: center;
                        align-items: center;
                        padding: 16px;
                        color: #8484a8;
                      `}
                    >
                      <Icon>
                        <ReportProblemIcon />
                      </Icon>
                      <QSTypography
                        css={css`
                          margin: 0px;
                          font-family: Roboto, sans-serif;
                          font-weight: 400;
                          font-size: 1rem;
                          line-height: 1.5;
                          text-align: center;
                        `}
                      >
                        No WABA's found
                      </QSTypography>
                    </div>
                  );
                },
                Footer: () => {
                  if (!fetchingMoreWabaIntegrations) {
                    return null;
                  }
                  return (
                    <div
                      css={css`
                        height: 100%;
                        width: 100%;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        padding: 16px;
                      `}
                    >
                      <Loader size={24} />
                    </div>
                  );
                },
              }}
            />
          </div>
        </div>
        <div
          css={css`
            display: flex;
            padding: 12px 20px;
            flex-direction: column;
            align-items: flex-end;
            gap: 12px;
            align-self: stretch;
            border-top: 1px solid #e0e0e9;
            background: #fff;
          `}
        >
          <div
            css={css`
              display: flex;
              justify-content: flex-end;
              align-items: flex-start;
              gap: 12px;
              align-self: stretch;
            `}
          >
            <QSButton
              css={css`
                display: flex;
                height: 40px;
                padding: 12px 16px;
                justify-content: center;
                align-items: center;
                gap: 8px;
                border-radius: 8px;
                border: 1px solid #e0e0e9;
                background: #fff;
                color: inherit;
                outline: none;
                box-shadow: none;

                &:hover {
                  background: #f5f5f8;
                  outline: none;
                  box-shadow: none;
                }
              `}
              onClick={onClose}
            >
              Cancel
            </QSButton>
            <QSButton
              css={css`
                display: flex;
                height: 40px;
                padding: 12px 16px;
                justify-content: center;
                align-items: center;
                gap: 8px;
                border-radius: 8px;
                background: #538d6f;
                outline: none;
                box-shadow: none;

                &:hover {
                  background: #538d6f;
                  outline: none;
                  box-shadow: none;
                }
              `}
              onClick={onConfirm}
            >
              Confirm
            </QSButton>
          </div>
        </div>
      </div>
    </QSModal>
  );
};

export default WabaSelectorModal;
